// src/components/auth/SocialAuthButton.tsx
import React from 'react';
import { SocialProvider } from '../../types/auth';
import { socialAuthService } from '../../services/socialAuthService';
import { Github, Linkedin, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

interface SocialAuthButtonProps {
  provider: SocialProvider;
  className?: string;
}

const SocialAuthButton: React.FC<SocialAuthButtonProps> = ({ 
  provider,
  className = '' 
}) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const handleAuth = async () => {
    setIsLoading(true);
    try {
      socialAuthService.initiateOAuth(provider);
    } catch (error) {
      console.error('Failed to initiate social auth:', error);
      setIsLoading(false);
    }
  };

  const getProviderName = () => {
    switch (provider) {
      case 'github':
        return 'Continue with GitHub';
      case 'linkedin':
        return 'Continue with LinkedIn';
      case 'google':
        return 'Continue with Google';
      default:
        return provider;
    }
  };

  const getProviderColor = () => {
    switch (provider) {
      case 'google':
        return 'hover:border-red-400 hover:text-red-600 focus:ring-red-500';
      case 'github':
        return 'hover:border-gray-800 hover:text-gray-800 focus:ring-gray-500';
      case 'linkedin':
        return 'hover:border-blue-600 hover:text-blue-600 focus:ring-blue-500';
      default:
        return '';
    }
  };

  const getIcon = () => {
    switch (provider) {
      case 'github':
        return <Github className="mr-3 h-5 w-5" />;
      case 'linkedin':
        return <Linkedin className="mr-3 h-5 w-5" />;
      case 'google':
        return (
          <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
            <path fill="none" d="M1 1h22v22H1z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <button
      onClick={handleAuth}
      disabled={isLoading}
      className={cn(
        'flex items-center justify-center px-4 py-3 border text-sm font-medium rounded-lg',
        'transition-all duration-200 transform',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'hover:scale-[1.02] active:scale-[0.98]',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        getProviderColor(),
        className
      )}
    >
      {isLoading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <>
          {getIcon()}
          <span className="font-semibold">{getProviderName()}</span>
        </>
      )}
    </button>
  );
};

export default SocialAuthButton;
